import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  PieChart,
  Pie,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import { Users, CreditCard, Home, BookOpen, ChevronRight } from "lucide-react";
import axios from "axios";
import "../css/Dashboard.css";

const Dashboard = () => {
  // State for dashboard data
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    summary: {
      totalStudents: 0,
      collegeFees: 0,
      hostelFees: 0,
      tuitionFees: 0,
    },
    chartData: {
      feeData: [],
      pieData: [],
      lineData: [],
    },
  });

  // Fetch dashboard data when component mounts
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          "https://payroll-api.uniums.com/api/dashboard/dashboard-data"
        );
        setDashboardData(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Format currency for display
  const formatCurrency = (amount) => {
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  if (loading) {
    return <div className="loading">Loading dashboard data...</div>;
  }

  return (
    <div className="dashboard">
      <div className="content">
        {/* Summary Cards using Link components */}
        <div className="dashboard-cards">
          <Link to="/students" className="dashboard-card">
            <div className="card-icon">
              <Users size={24} />
            </div>
            <div className="card-content">
              <h3>Total Students</h3>
              <p className="card-value">
                {dashboardData.summary.totalStudents.toLocaleString()}
              </p>
            </div>
            <div className="card-arrow">
              <ChevronRight size={16} />
            </div>
          </Link>

          <Link to="/college-fees-bill" className="dashboard-card">
            <div className="card-icon">
              <CreditCard size={24} />
            </div>
            <div className="card-content">
              <h3>College Fees</h3>
              <p className="card-value">
                {formatCurrency(dashboardData.summary.collegeFees)}
              </p>
            </div>
            <div className="card-arrow">
              <ChevronRight size={16} />
            </div>
          </Link>

          <Link to="/hostel/generate-bill" className="dashboard-card">
            <div className="card-icon">
              <Home size={24} />
            </div>
            <div className="card-content">
              <h3>Hostel Fees</h3>
              <p className="card-value">
                {formatCurrency(dashboardData.summary.hostelFees)}
              </p>
            </div>
            <div className="card-arrow">
              <ChevronRight size={16} />
            </div>
          </Link>

          <Link to="/tuition/generate-bill" className="dashboard-card">
            <div className="card-icon">
              <BookOpen size={24} />
            </div>
            <div className="card-content">
              <h3>Tuition Fees</h3>
              <p className="card-value">
                {formatCurrency(dashboardData.summary.tuitionFees)}
              </p>
            </div>
            <div className="card-arrow">
              <ChevronRight size={16} />
            </div>
          </Link>
        </div>

        {/* Charts Section */}
        <div className="charts-section">
          <div className="chart-container">
            <h3>Fee Collection Overview</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dashboardData.chartData.feeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="college" name="College Fees" fill="#8884d8" />
                <Bar dataKey="hostel" name="Hostel Fees" fill="#82ca9d" />
                <Bar dataKey="tuition" name="Tuition Fees" fill="#ffc658" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-container">
            <h3>Fee Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={dashboardData.chartData.pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Additional Graphs Section */}
        <div className="additional-graphs">
          <div className="graph-container">
            <h3>Student Enrollment Trend</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={dashboardData.chartData.lineData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="students"
                  stroke="#8884d8"
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="graph-container">
            <h3>Monthly Fee Collection Rate</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={dashboardData.chartData.feeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="college" name="College Fees" fill="#8884d8" />
                <Bar dataKey="hostel" name="Hostel Fees" fill="#82ca9d" />
                <Bar dataKey="tuition" name="Tuition Fees" fill="#ffc658" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
